// React Query hooks para gerenciar group_topic_blacklist via RPCs
// SECURITY DEFINER. Toda autorização acontece no banco — o front só
// renderiza erros amigáveis quando RPC retorna 'not_group_admin' etc.

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface BlacklistEntry {
  id: string;
  org_id: string;
  group_id: string;
  topic_label: string;
  reason: string | null;
  active: boolean;
  created_at: string;
  created_by: string | null;
}

export interface BlacklistTemplate {
  id: string;
  topic_label: string;
  category: "political" | "religious" | "nsfw" | "health_claims" | "financial_advice" | "violence" | "personal_data";
  description_pt: string;
  default_severity: "soft" | "hard";
}

export function useGroupBlacklist(groupId: string | undefined) {
  return useQuery({
    queryKey: ["group-blacklist", groupId],
    queryFn: async (): Promise<BlacklistEntry[]> => {
      if (!groupId) return [];
      const { data, error } = await supabase
        .from("group_topic_blacklist")
        .select("id, org_id, group_id, topic_label, reason, active, created_at, created_by")
        .eq("group_id", groupId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as BlacklistEntry[];
    },
    enabled: !!groupId,
  });
}

export function useBlacklistTemplates() {
  return useQuery({
    queryKey: ["blacklist-templates"],
    queryFn: async (): Promise<BlacklistTemplate[]> => {
      const { data, error } = await supabase
        .from("blacklist_topic_templates")
        .select("id, topic_label, category, description_pt, default_severity")
        .order("category")
        .order("topic_label");
      if (error) throw error;
      return (data ?? []) as BlacklistTemplate[];
    },
  });
}

export function useAddBlacklistTopic(groupId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (topicLabel: string) => {
      const { data, error } = await supabase.rpc("add_blacklist_topic", {
        _group_id: groupId,
        _topic_label: topicLabel,
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["group-blacklist", groupId] }),
  });
}

export function useToggleBlacklistEntry(groupId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ entryId, active }: { entryId: string; active: boolean }) => {
      const { error } = await supabase.rpc("toggle_blacklist_entry", {
        _entry_id: entryId,
        _active: active,
      });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["group-blacklist", groupId] }),
  });
}

export function useImportBlacklistTemplates(groupId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (templateIds: string[]) => {
      const { data, error } = await supabase.rpc("import_blacklist_template", {
        _group_id: groupId,
        _template_ids: templateIds,
      });
      if (error) throw error;
      // RPC retorna setof (imported_count, skipped_count)
      const row = Array.isArray(data) ? data[0] : data;
      return row as { imported_count: number; skipped_count: number };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["group-blacklist", groupId] }),
  });
}

export function mapRpcError(err: unknown): string {
  const msg = (err as { message?: string })?.message ?? "";
  if (msg.includes("not_group_admin")) return "Você precisa ser admin deste grupo para realizar esta ação.";
  if (msg.includes("unauthenticated")) return "Sessão expirada. Faça login novamente.";
  if (msg.includes("group_not_found")) return "Grupo não encontrado.";
  if (msg.includes("entry_not_found")) return "Entrada não encontrada.";
  if (msg.includes("invalid_topic_label")) return "Tópico inválido (precisa entre 2 e 80 caracteres).";
  return "Algo deu errado. Tente novamente em instantes.";
}
